import { useMemo, useState, type DragEvent, type MouseEvent } from "react";
import clsx from "clsx";
import type { SupportTicket, TicketUrgency } from "@/lib/support";
import { updateTicketTodo, updateTicketUrgency } from "@/lib/support";
import { BOARD_COLUMNS, URGENCY_LABEL, URGENCY_OPTIONS } from "@/pages/support/constants";
import { useSyncedHorizontalScroll } from "@/pages/support/useSyncedHorizontalScroll";
import { IncomingReplyDot, ReplyMailIcon } from "@/pages/support/SupportBadges";
import {
  fmtRelative,
  normalizeTodoState,
  sortBoardColumnTickets,
  ticketHasOutboundReply,
  ticketHasUnreadIncomingReply,
  toastErr,
  toastOk,
} from "@/pages/support/utils";

const DND_MIME = "application/x-support-ticket-id";
const COLUMN_W = "17.5rem";

export function AdminKanbanBoard({
  tickets,
  selectedId,
  onSelect,
  onTicketPatched,
  onTicketContextMenu,
}: {
  tickets: SupportTicket[];
  selectedId: number | null;
  onSelect: (t: SupportTicket) => void;
  onTicketPatched: (ticketId: number, patch: Partial<SupportTicket>) => void;
  onTicketContextMenu?: (ticket: SupportTicket, event: MouseEvent) => void;
}) {
  const [dragOverColumn, setDragOverColumn] = useState<string | null>(null);
  const [draggingId, setDraggingId] = useState<number | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const { primaryRef, secondaryRef, onPrimaryScroll, onSecondaryScroll } =
    useSyncedHorizontalScroll();

  const columns = useMemo(() => {
    const map = new Map<string, SupportTicket[]>();
    for (const col of BOARD_COLUMNS) map.set(col.key, []);
    for (const ticket of tickets) {
      const state = normalizeTodoState(ticket.todo_state);
      const list = map.get(state) || map.get("eingang")!;
      list.push(ticket);
    }
    for (const [key, list] of map) {
      map.set(key, sortBoardColumnTickets(list));
    }
    return map;
  }, [tickets]);

  async function moveTicket(ticket: SupportTicket, nextState: string) {
    const current = normalizeTodoState(ticket.todo_state);
    if (current === nextState) return;
    setBusyId(ticket.id);
    onTicketPatched(ticket.id, { todo_state: nextState, is_unread: false });
    try {
      await updateTicketTodo(ticket.id, { todo_state: nextState });
      toastOk(`Nach „${BOARD_COLUMNS.find((c) => c.key === nextState)?.label || nextState}“ verschoben`);
    } catch (err) {
      console.error(err);
      onTicketPatched(ticket.id, { todo_state: current });
      toastErr("Verschieben fehlgeschlagen");
    } finally {
      setBusyId(null);
    }
  }

  async function changeUrgency(ticket: SupportTicket, urgency: TicketUrgency) {
    const prev = String(ticket.urgency || "normal");
    if (prev === urgency) return;
    setBusyId(ticket.id);
    onTicketPatched(ticket.id, { urgency, is_unread: false });
    try {
      await updateTicketUrgency(ticket.id, urgency);
      toastOk(`Dringlichkeit: ${URGENCY_LABEL[urgency] || urgency}`);
    } catch (err) {
      console.error(err);
      onTicketPatched(ticket.id, { urgency: prev });
      toastErr("Dringlichkeit konnte nicht geändert werden");
    } finally {
      setBusyId(null);
    }
  }

  function handleColumnDragOver(e: DragEvent, colKey: string) {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (dragOverColumn !== colKey) setDragOverColumn(colKey);
  }

  function handleColumnDragLeave(e: DragEvent, colKey: string) {
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setDragOverColumn((prev) => (prev === colKey ? null : prev));
    }
  }

  function handleColumnDrop(e: DragEvent, colKey: string) {
    e.preventDefault();
    setDragOverColumn(null);
    setDraggingId(null);
    const raw = e.dataTransfer.getData(DND_MIME) || e.dataTransfer.getData("text/plain");
    const id = Number(raw);
    if (!Number.isFinite(id)) return;
    const ticket = tickets.find((t) => Number(t.id) === id);
    if (!ticket) return;
    void moveTicket(ticket, colKey);
  }

  return (
    <div className="support-kanban-wrap">
      <div className="support-kanban-head">
        <div
          ref={secondaryRef}
          className="support-kanban-head-scroller"
          onScroll={onSecondaryScroll}
        >
          <div className="flex items-stretch gap-3">
            {BOARD_COLUMNS.map((col) => {
              const items = columns.get(col.key) || [];
              const isOver = dragOverColumn === col.key;
              return (
                <header
                  key={col.key}
                  className={clsx(
                    "flex shrink-0 items-center justify-between gap-2 border-b px-3 py-2 backdrop-blur-sm",
                    "rounded-t-ios-lg border-x border-t bg-grouped/95",
                    isOver ? "border-accent" : "border-separator",
                  )}
                  style={{ width: COLUMN_W }}
                  onDragOver={(e) => handleColumnDragOver(e, col.key)}
                  onDragLeave={(e) => handleColumnDragLeave(e, col.key)}
                  onDrop={(e) => handleColumnDrop(e, col.key)}
                >
                  <h3 className="text-sm font-semibold text-label">{col.label}</h3>
                  <span className="rounded-full bg-fill-tertiary px-2 py-0.5 text-[11px] font-semibold tabular-nums text-label-secondary">
                    {items.length}
                  </span>
                </header>
              );
            })}
          </div>
        </div>
      </div>

      <div
        ref={primaryRef}
        className="support-kanban flex items-start gap-3 overflow-x-auto pb-3"
        onScroll={onPrimaryScroll}
      >
        {BOARD_COLUMNS.map((col) => {
          const items = columns.get(col.key) || [];
          const isOver = dragOverColumn === col.key;
          return (
            <section
              key={col.key}
              className={clsx(
                "support-kanban-column flex shrink-0 flex-col rounded-b-ios-lg border-x border-b bg-fill-quaternary/20",
                isOver ? "border-accent ring-2 ring-accent/30" : "border-separator",
              )}
              style={{ width: COLUMN_W }}
              onDragOver={(e) => handleColumnDragOver(e, col.key)}
              onDragLeave={(e) => handleColumnDragLeave(e, col.key)}
              onDrop={(e) => handleColumnDrop(e, col.key)}
            >
              <div className="support-kanban-column__body flex min-h-[6rem] flex-col gap-2 p-2">
                {!items.length ? (
                  <p className="px-1 py-6 text-center text-xs text-label-tertiary">Keine Tickets</p>
                ) : (
                  items.map((ticket) => (
                    <KanbanCard
                      key={ticket.id}
                      ticket={ticket}
                      selected={selectedId === ticket.id}
                      busy={busyId === ticket.id}
                      dragging={draggingId === ticket.id}
                      onSelect={() => onSelect(ticket)}
                      onContextMenu={
                        onTicketContextMenu
                          ? (e) => onTicketContextMenu(ticket, e)
                          : undefined
                      }
                      onDragStart={() => setDraggingId(ticket.id)}
                      onDragEnd={() => {
                        setDraggingId(null);
                        setDragOverColumn(null);
                      }}
                      onChangeUrgency={(u) => void changeUrgency(ticket, u)}
                    />
                  ))
                )}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
}

function KanbanCard({
  ticket,
  selected,
  busy,
  dragging,
  onSelect,
  onContextMenu,
  onDragStart,
  onDragEnd,
  onChangeUrgency,
}: {
  ticket: SupportTicket;
  selected: boolean;
  busy: boolean;
  dragging: boolean;
  onSelect: () => void;
  onContextMenu?: (event: MouseEvent) => void;
  onDragStart: () => void;
  onDragEnd: () => void;
  onChangeUrgency: (u: TicketUrgency) => void;
}) {
  const urgency = String(ticket.urgency || "normal").toLowerCase() as TicketUrgency;
  const reporter = String(ticket.reporter_display || ticket.reporter_login || "").trim();
  const updated = ticket.last_reply_at || ticket.updated_at || ticket.created_at;
  const hasNote = Boolean(String(ticket.my_note || "").trim());
  const tags = Array.isArray(ticket.tags) ? ticket.tags : [];
  const incomingReply = ticketHasUnreadIncomingReply(ticket);
  const hasReplyMail = ticketHasOutboundReply(ticket);

  return (
    <article
      draggable={!busy}
      onDragStart={(e) => {
        e.dataTransfer.setData(DND_MIME, String(ticket.id));
        e.dataTransfer.setData("text/plain", String(ticket.id));
        e.dataTransfer.effectAllowed = "move";
        onDragStart();
      }}
      onDragEnd={onDragEnd}
      onContextMenu={(e) => {
        if ((e.target as HTMLElement).closest("select")) return;
        if (!onContextMenu) return;
        e.preventDefault();
        e.stopPropagation();
        onContextMenu(e);
      }}
      className={clsx(
        "support-kanban-card cursor-grab rounded-ios border p-2.5 text-left shadow-sm transition active:cursor-grabbing",
        selected
          ? "border-accent ring-1 ring-accent/40"
          : "border-separator/70 hover:border-separator",
        incomingReply && "support-kanban-card--incoming-reply",
        ticket.is_unread && !incomingReply && "support-kanban-card--unread",
        dragging && "opacity-50",
        busy && "pointer-events-none opacity-60",
      )}
    >
      <button type="button" className="w-full text-left" onClick={onSelect}>
        <div className="flex items-start gap-2">
          {incomingReply ? (
            <IncomingReplyDot className="mt-1.5" />
          ) : ticket.is_unread ? (
            <span
              className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-accent"
              title="Ungelesen"
              aria-label="Ungelesen"
            />
          ) : (
            <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-transparent" aria-hidden />
          )}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5 text-[11px] text-label-tertiary">
              <span className="font-mono">#{ticket.id}</span>
              {hasReplyMail ? <ReplyMailIcon /> : null}
              {hasNote ? <span title="Private Notiz vorhanden">📝</span> : null}
            </div>
            <p
              className={clsx(
                "mt-0.5 line-clamp-2 text-sm text-label",
                ticket.is_unread ? "font-semibold" : "font-medium",
              )}
            >
              {ticket.title || "—"}
            </p>
          </div>
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-1 text-[11px] text-label-secondary">
          <span className="truncate">{reporter || "Unbekannt"}</span>
          <span>·</span>
          <span title={updated || ""}>{fmtRelative(updated)}</span>
        </div>
        {tags.length ? (
          <div className="mt-2 flex flex-wrap gap-1">
            {tags.slice(0, 4).map((tag) => (
              <span
                key={tag.id}
                className="max-w-[7rem] truncate rounded-full bg-fill-tertiary px-1.5 py-0.5 text-[10px] font-medium text-label"
                title={tag.name}
              >
                {tag.name}
              </span>
            ))}
            {tags.length > 4 ? (
              <span className="text-[10px] text-label-tertiary">+{tags.length - 4}</span>
            ) : null}
          </div>
        ) : null}
      </button>
      <div className="mt-2 flex items-center gap-1" data-no-drag>
        <label className="sr-only" htmlFor={`urgency-${ticket.id}`}>
          Dringlichkeit
        </label>
        <select
          id={`urgency-${ticket.id}`}
          className={clsx(
            "eh-input max-w-full py-0.5 text-[11px]",
            urgency === "critical" && "text-status-red",
            urgency === "high" && "text-status-orange",
          )}
          value={urgency}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => onChangeUrgency(e.target.value as TicketUrgency)}
          draggable={false}
        >
          {URGENCY_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>
    </article>
  );
}
